import styles from './step2.module.scss';
import { Button, TextField } from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useValidator } from 'hooks/useValidator';
import { useTranslation } from 'react-i18next';
import Popover from '@mui/material/Popover';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import actions from 'store/actions';
import { updateNA, updateExternalAccessNA } from 'store/na/thunk';
import getDefaultSharedSeatsRatio from 'services/inSite/sharedSeatsRatio';
import NumberFormatCustom from 'components/common/NumberFormatCustom';

export default function SharedSeatsRatios(props) {
  const { disabled, isExternalAccess } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const na = useSelector((state) => state.na);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sharedSeatsRatio, setSharedSeatsRatio] = useState(na.sharedSeatsRatio);
  const [isEditableRatios, setIsEditableRatios] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleClickPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setIsEditableRatios(false);
    setIsConfirmed(false);
    setErrorMessage('');
  };

  const handleChangingRatios = (event, days) => {
    setSharedSeatsRatio((prevState) => {
      return prevState.map((obj) => {
        if (obj.day === days) {
          return {
            ...obj,
            ratio: event.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'),
          };
        } else {
          return obj;
        }
      });
    });
  };

  const openPopover = Boolean(anchorEl);
  const id = openPopover ? 'simple-popover' : undefined;

  const [validables, validator] = useValidator(
    sharedSeatsRatio?.map((item) => `ratio_field_${item.day}`),
    () => {
      const message = {};
      sharedSeatsRatio.forEach((item) => {
        if (item.ratio < 1 || item.ratio > 5)
          message[`ratio_field_${item.day}`] = t('step2.shareSeatsError', { min: 1, max: 5 });
      });
      setErrorMessage(Object.values(message)[0]);
      return message;
    },
  );

  const onCancel = () => {
    if (isConfirmed && na.sharedSeatsRatio) {
      setSharedSeatsRatio(getDefaultSharedSeatsRatio());
      dispatch(actions.na.updateRatios(getDefaultSharedSeatsRatio()));
      setIsConfirmed(false);
    } else if (!isConfirmed && isEditableRatios) {
      handleClosePopover();
      setSharedSeatsRatio(na.sharedSeatsRatio);
    } else {
      handleClosePopover();
    }
  };

  const onConfirm = () => {
    if (!validator.validate()) return;
    setIsEditableRatios(!isEditableRatios);
    setIsConfirmed(false);
    if (isEditableRatios) {
      dispatch(actions.na.updateRatios(sharedSeatsRatio));
      dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: true }) : updateNA);
      setIsConfirmed(true);
    }
  };

  return (
    <div className={styles.ratios}>
      <Button
        data-testid="RatioToggleBtn"
        aria-describedby={id}
        variant="contained"
        className={styles.ratios__settings_button}
        onClick={handleClickPopover}>
        <SettingsOutlinedIcon />
      </Button>
      <Popover
        id={id}
        open={openPopover}
        data-testid="RatioPopover"
        anchorEl={anchorEl}
        onClose={!isEditableRatios ? handleClosePopover : () => {}}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}>
        <div className={styles.ratios__container}>
          <div className={styles.title}>{t('step2.hybridTitle')}</div>
          <div className={styles.description}>{t('step2.hybridDescription')}</div>
          <div className={styles.errorline}>{errorMessage}</div>
          <table className={styles.table}>
            <tbody>
              <tr>
                <th></th>
                <th>{t('step2.employees')}</th>
                <th>{t('step2.seats')}</th>
              </tr>
              {sharedSeatsRatio?.map((item) => (
                <tr key={item.day}>
                  <td>
                    {item.day} {item.day === 1 ? t('step2.dayWeek') : t('step2.daysWeek')}
                  </td>
                  <td>
                    <TextField
                      error={!validables[`ratio_field_${item.day}`]?.valid}
                      disabled={!isEditableRatios || item.day > 3}
                      value={item.ratio}
                      className={`${styles.ratios__input} ${
                        (item.day > 3 || !isEditableRatios) && styles.ratios__input_inactive
                      }`}
                      onChange={(event) => handleChangingRatios(event, item.day)}
                      InputProps={{
                        inputComponent: NumberFormatCustom,
                      }}
                      inputProps={{
                        'data-testid': `ratio_field_${item.day}`,
                        style: {
                          height: '28px',
                          padding: '0 12px',
                        },
                        decimalSeparator: true,
                        maxLength: 4,
                      }}
                    />
                  </td>
                  <td>{item.seats}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.ratios__buttons}>
            <Button
              data-testid="RatioCancelBtn"
              className={styles.ratios__secondary_btn}
              variant="contained"
              color="secondary"
              onClick={onCancel}>
              {isConfirmed ? t('common.reset') : t('common.cancel')}
            </Button>
            <Button
              disabled={disabled}
              data-testid="RatioConfirmBtn"
              className={styles.ratios__primary_btn}
              variant="contained"
              color="primary"
              onClick={onConfirm}>
              {isEditableRatios ? t('common.confirm') : t('step2.editRatios')}
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
